import { Component, computed, ElementRef, inject, input, OnInit, signal, viewChild } from "@angular/core";
import { SpinnerComponent } from "../spinner/spinner.component";
import { CommonModule } from "@angular/common";
import { DocumentHelper } from "../../helper/document";
import { ChannelService, ParameterService, PaymentService } from "../../provider";
import { lastValueFrom } from "rxjs";

declare const paypal: any;
let globalthis: any;

function onClick()
    {
        const paymentRequest = {
            countryCode: globalthis.countryCode,
            currencyCode: 'EUR',
            merchantCapabilities: globalthis.merchantCapabilities,
            supportedNetworks: globalthis.supportedNetworks,
            requiredBillingContactFields: [ "name", "phone", "email", "postalAddress", ],
            requiredShippingContactFields: [],
            total: {
                label: globalthis.channelService.getReferenceTextWithId(),
                amount: globalthis.data().amount,
                type: "final",
            },
        };

        // eslint-disable-next-line no-undef
        let session = new (window as any).ApplePaySession(4, paymentRequest);

        session.onvalidatemerchant = (event: any) => {
            globalthis.applepay.validateMerchant({
                validationUrl: event.validationURL,
            })
            .then((payload: any) => {
                session.completeMerchantValidation(payload.merchantSession);
            })
            .catch((err: any) => {
                console.error(err);
                session.abort();
            });
        };

        session.onpaymentmethodselected = (event: any) => {
            session.completePaymentMethodSelection({
                newTotal: paymentRequest.total,
            });
        };

        session.onpaymentauthorized = async (event: any) => {
            try {
                /* Create Order on the Server Side */
                await globalthis.startOrder(session, event);
            } 
            catch (err: any) {
                console.error(err);
                session.completePayment({
                    status: (window as any).ApplePaySession.STATUS_FAILURE,
                });
            }
        };

        session.oncancel = (event: any) => {
            console.log("Apple Pay Cancelled !!");
            console.log(event);
        }

        session.begin();
    }

@Component({
    selector: 'lib-frontend-applepay',
    templateUrl: './applepay.html',
    styles: 'lib-frontend-spinner { margin: 0px auto; }',
    standalone: true,
    imports: [
        CommonModule,
        SpinnerComponent,
    ]
})
export class ApplePay implements OnInit
{
    
    data = input({} as any);
    applepayContainer = viewChild.required<ElementRef>('applepayContainer');
    loading = signal(true);
    internalSubscription: any;
    counter = 0;
    showError = signal(false);
    showErrorInPaymentprocess = signal(false);
    backUrl = computed(() => this.parameterService.getOverviewUrl());
    applePaySessionObj: any;
    currentId: string | undefined;
    countryCode: any;
    currencyCode: any;
    merchantCapabilities: any;
    supportedNetworks: any;
    applepay: any;

    private parameterService = inject(ParameterService);
    private paymentService = inject(PaymentService);
    public readonly channelService = inject(ChannelService);

    ngOnInit(): void {
      this.loadPaypalScript();
    }
    
    loadPaypalScript()
    {
        DocumentHelper.loadExternalScript(
            `https://www.paypal.com/sdk/js?components=applepay&client-id=${this.data().client_id}`,
            this, 
            'loadApplePayScript'
        );
    }

    loadApplePayScript()
    {
        DocumentHelper.loadExternalScript(
            'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js',
            this,
            'onApplePayLoaded'
        );
    }

    async onApplePayLoaded(): Promise<void> {
        if((window as any).ApplePaySession?.supportsVersion(4) && (window as any).ApplePaySession?.canMakePayments()) {
            await this.setupApplepay();
        }
        else {
            this.showError.set(true);
        }
    }

    async setupApplepay(): Promise<void> {
        this.applepay = paypal.Applepay();
        const {
            isEligible,
            countryCode,
            currencyCode,
            merchantCapabilities,
            supportedNetworks,
        } = await this.applepay.config();

        this.countryCode = countryCode;
        this.currencyCode = currencyCode;
        this.merchantCapabilities = merchantCapabilities;
        this.supportedNetworks = supportedNetworks;

        if (!isEligible) {
            throw new Error("applepay is not eligible");
        }

        this.applepayContainer().nativeElement.innerHTML = '<apple-pay-button id="btn-appl" buttonstyle="black" type="buy" locale="de">';        
        document.getElementById("btn-appl")?.addEventListener("click", onClick);
        this.loading.set(false);
        globalthis = this;
    }

    public async startOrder(sessionObj: any, event: any) {
        this.applePaySessionObj = sessionObj;
        const data = await lastValueFrom(this.paymentService.getCreateOrderForPaypal(this.parameterService.queryParams.channel_id, this.parameterService.queryParams.payment_id ?? ''));
        this.currentId = data.data ?? '';
        if (this.currentId)
        {
            await new this.applepay.confirmOrder({ orderId: this.currentId, token: event.payment.token, billingContact: event.payment.billingContact , shippingContact: event.payment.shippingContact });
            const resp = await lastValueFrom(this.paymentService.capture(this.parameterService.queryParams.channel_id, this.parameterService.queryParams.payment_id ?? '', {
                seccode: this.currentId
            }));
            sessionObj.completePayment({
                status: (window as any).ApplePaySession.STATUS_SUCCESS,
            });
            this.forwardToSuccess();
        }
        else {
            sessionObj.completePayment({
                status: (window as any).ApplePaySession.STATUS_FAILURE,
            });
            this.showErrorInPaymentprocess.set(true);
        }
    }

    public forwardToSuccess(): void
    {
        window.location.href = `${window.location.pathname}?channel=${this.parameterService.queryParams.channel_id}&payment=${this.parameterService.queryParams.payment_id ?? ''}`;
    }

}